import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import ApiVideoPlayer from "@api.video/react-player";
import Spinner from "Components/RequestHandler/Spinner";
import useVideoProgress from "Hooks/useVideoProgress";
import useVideoHistory from "Hooks/useVideoHistory";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import { useTranslation } from "react-i18next";
import formatTime from "Utilities/formatTime";
import { bigThumb } from "Utilities/courseImages";
import VideoPlayerSkeleton from "../Common/VideoPlayerSkeleton";
import UpNextOverlay from "../Common/UpNextOverlay";

/**
 * VideoPlayer component
 * - Shows resume modal based on video_progress from API (no localStorage)
 * - Tracks progress and marks videos as done via API
 */
const VideoPlayer = ({
  videoData,
  videoId,
  courseId,
  courseSlug,
  videoProgress, // { timestamp, timeSlap } from API - for resume modal
  initialIsDone, // boolean from API - if video is already done
  onProgressUpdate,
  onVideoCompleted,
  onEnded,
  nextItem,
  autoPlayOnLoad = false,
  hasQuiz = false,
  onQuizRequired,
  triggerUpNextNonce = 0,
}) => {
  const { t } = useTranslation();
  const { selectedUser, user, isAuthenticated } = useContext(LoginAuthContext);

  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [showUpNext, setShowUpNext] = useState(false);

  const playerRef = useRef(null);
  const durationRef = useRef(0);
  const currentTimeRef = useRef(0);
  const currentVideoIdRef = useRef(videoId);
  const lastCheckedVideoRef = useRef(null);
  const lastCheckedProgressRef = useRef(null);
  const lastVideoIdRef = useRef(null);

  // Update the videoId ref whenever the prop changes
  useEffect(() => {
    currentVideoIdRef.current = videoId;
  }, [videoId]);

  // Use our progress tracking hook (for marking videos as done at 75%)
  const { updateProgress, isVideoDone } = useVideoProgress(courseId, videoId, initialIsDone);

  // Store callbacks in refs to avoid dependency issues
  const onVideoCompletedRef = useRef(onVideoCompleted);
  useEffect(() => {
    onVideoCompletedRef.current = onVideoCompleted;
  }, [onVideoCompleted]);

  // Notify parent when video is marked as done
  const notifiedRef = useRef(false);
  useEffect(() => {
    // Reset notification flag when video changes
    notifiedRef.current = false;
  }, [videoId]);

  useEffect(() => {
    if (isVideoDone && onVideoCompletedRef.current && !notifiedRef.current) {
      notifiedRef.current = true;
      onVideoCompletedRef.current(videoId);
    }
  }, [isVideoDone, videoId]);

  // Use our video history hook (for continue watching)
  // Pass isVideoDone to stop saving progress once video is completed.
  // The hook persists the outgoing video's position on switch/unmount itself.
  const { updateCurrentTime } = useVideoHistory(
    courseSlug,
    videoId,
    duration,
    isVideoDone
  );

  // Reset state when video changes
  useEffect(() => {
    const isVideoChange = lastVideoIdRef.current !== null && lastVideoIdRef.current !== videoId;

    if (isVideoChange) {
      // Only reset these when switching videos
      setShowResumePrompt(false);
      setSavedProgress(null);
      setShowUpNext(false);
      lastCheckedVideoRef.current = null;
      lastCheckedProgressRef.current = null;
      durationRef.current = 0;
      currentTimeRef.current = 0;
    }

    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 500);

    lastVideoIdRef.current = videoId;
    return () => clearTimeout(timer);
  }, [videoId]);

  // Check for saved progress from API when video loads
  useEffect(() => {
    if (!videoId) return;

    // Get the timestamp from videoProgress (0 if no progress)
    const progressTimestamp = videoProgress?.timestamp || 0;

    // Skip if we already checked this exact video + progress combination
    if (
      lastCheckedVideoRef.current === videoId &&
      lastCheckedProgressRef.current === progressTimestamp
    ) {
      return;
    }

    // Mark as checked with current values
    lastCheckedVideoRef.current = videoId;
    lastCheckedProgressRef.current = progressTimestamp;

    // Show resume prompt if timestamp > 5 seconds
    if (progressTimestamp > 5) {
      setSavedProgress({
        timestamp: progressTimestamp,
        timeSlap: videoProgress.timeSlap || formatTime(progressTimestamp),
      });
      setShowResumePrompt(true);
    }
  }, [videoId, videoProgress]);

  // Handle resume - seek to saved position
  const handleResume = () => {
    setShowResumePrompt(false);
    if (playerRef.current && savedProgress?.timestamp) {
      // Use setTimeout to ensure player is ready
      setTimeout(() => {
        try {
          playerRef.current.setCurrentTime(savedProgress.timestamp);
          playerRef.current.play();
        } catch (e) {
          console.error("Error seeking:", e);
        }
      }, 500);
    }
  };

  // Handle start from beginning
  const handleStartOver = () => {
    setShowResumePrompt(false);
    setSavedProgress(null);
  };

  // Advance to the next lesson/episode — via countdown or "Play Now"
  const handleConfirmNext = useCallback(() => {
    setShowUpNext(false);
    if (typeof onEnded === "function") onEnded(currentVideoIdRef.current);
  }, [onEnded]);

  // Stay on the ended video — cancel the auto-advance
  const handleCancelNext = () => setShowUpNext(false);

  // Handle when player is ready
  const handleReady = () => {
    setIsReady(true);
    if (autoPlayOnLoad && playerRef.current) {
      setTimeout(() => {
        try {
          playerRef.current?.play();
        } catch (e) {
          console.error("Autoplay failed:", e);
        }
      }, 300);
    }
  };

  // Handle when duration changes
  const handleDurationChange = (newDuration) => {
    setDuration(newDuration);
    durationRef.current = newDuration;
  };

  // Store onProgressUpdate in a ref
  const onProgressUpdateRef = useRef(onProgressUpdate);
  useEffect(() => {
    onProgressUpdateRef.current = onProgressUpdate;
  }, [onProgressUpdate]);

  // The api.video player captures event handlers at mount, so `handleEnded`
  // would otherwise see a stale `hasQuiz` (false, from before the quiz loaded).
  // Mirror the latest values into refs and read them when the video ends.
  const hasQuizRef = useRef(hasQuiz);
  useEffect(() => {
    hasQuizRef.current = hasQuiz;
  }, [hasQuiz]);
  const onQuizRequiredRef = useRef(onQuizRequired);
  useEffect(() => {
    onQuizRequiredRef.current = onQuizRequired;
  }, [onQuizRequired]);

  // Custom progress handler
  const handleProgressUpdate = useCallback(
    (percent) => {
      updateProgress(percent);

      if (onProgressUpdateRef.current && percent > 0) {
        onProgressUpdateRef.current(currentVideoIdRef.current, percent);
      }
    },
    [updateProgress]
  );

  // Handle time update
  const handleTimeUpdate = (currentTime) => {
    currentTimeRef.current = currentTime;
    updateCurrentTime(currentTime);

    const videoDuration = durationRef.current;
    if (videoDuration > 0) {
      const percentComplete = Math.floor((currentTime / videoDuration) * 100);
      if (percentComplete > 0) {
        handleProgressUpdate(percentComplete);
      }
    }
  };

  // Mark complete on end. If this lesson has a quiz, hand off to the parent to
  // open the quiz modal (the "Up Next" popup is deferred until the quiz is done).
  // Otherwise behave as before: show "Up Next" or advance.
  const handleEnded = () => {
    updateProgress(100);
    if (hasQuizRef.current && typeof onQuizRequiredRef.current === "function") {
      onQuizRequiredRef.current();
      return;
    }
    if (nextItem) {
      setShowUpNext(true);
    } else if (typeof onEnded === "function") {
      onEnded(videoId);
    }
  };

  // Parent finished the quiz → now show the "Up Next" popup (then advance).
  useEffect(() => {
    if (triggerUpNextNonce && nextItem) setShowUpNext(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerUpNextNonce]);

  return (
    <>
      {/* Resume Prompt Modal - Fixed position to ensure visibility */}
      {showResumePrompt && savedProgress && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-[9999]">
          <div className="bg-gray-900 rounded-lg p-6 max-w-sm mx-4 text-center">
            <h3 className="text-white text-lg font-bold mb-2">
              {t("video.resume_watching") || "Resume Watching?"}
            </h3>
            <p className="text-gray-300 mb-4">
              {t("video.resume_message", { time: savedProgress.timeSlap }) ||
                `You stopped at ${savedProgress.timeSlap}. Would you like to continue from where you left off?`}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleStartOver}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                {t("video.start_over") || "Start Over"}
              </button>
              <button
                onClick={handleResume}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors"
              >
                {t("video.continue") || "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative w-full aspect-video bg-black overflow-hidden">
        {!isReady && (
          <VideoPlayerSkeleton
            thumbnail={bigThumb(
              videoData,
              videoData?.trailer?.assets?.thumbnail || videoData?.api_video_object?.assets?.thumbnail
            )}
          />
        )}

        {loading && isReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
            <Spinner />
          </div>
        )}

        {videoId && (
          <div className="absolute inset-0">
            <ApiVideoPlayer
              ref={playerRef}
              onReady={handleReady}
              onDurationChange={handleDurationChange}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              hideTitle
              video={{ id: videoId }}
              autoplay={false}
              responsive
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </div>
        )}

        {showUpNext && nextItem && (
          <UpNextOverlay
            nextItem={nextItem}
            onConfirm={handleConfirmNext}
            onCancel={handleCancelNext}
          />
        )}
      </div>

      <div className="header_container">
        <div className="flex flex-col mt-4 gap-y-4 mb-10 lg:pb-secondary">
          <h1 className="text-white text-2xl font-bold">{videoData?.name}</h1>
          <p
            className="text-white text-sm lg:w-1/2"
            dangerouslySetInnerHTML={{ __html: videoData?.description }}
          ></p>
        </div>
      </div>
    </>
  );
};

export default VideoPlayer;
